import { fmtDuration, isGeneralJob, jobModuleLabel, jobServiceScopeSummary, normaliseJobStatus } from './utils'
import { normaliseBookingServiceSnapshot, serviceModuleKey } from './serviceMenu'

export const CALENDAR_RANGE_DAYS = {
  twoWeeks: 14,
  fourWeeks: 28,
}

export const MODULE_CALENDAR_TONES = {
  general: 'blue',
  suspension: 'orange',
}

const ACTIVE_CAPACITY_STATUSES = new Set([
  'awaiting_customer_item',
  'received',
  'estimate_sent',
  'awaiting_parts',
  'ready_for_collection',
])

const HISTORICAL_CAPACITY_STATUSES = new Set([
  'completed',
  'closed',
  'collected',
])

const NEEDS_SCHEDULING_STATUSES = new Set([
  'awaiting_customer_item',
  'received',
  'estimate_sent',
  'awaiting_parts',
  'ready_for_collection',
])

const NON_CAPACITY_STATUSES = new Set(['cancelled', 'voided'])

function text(value) {
  return String(value || '').trim()
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function selectedServiceIds(row = {}) {
  return new Set(asArray(row.service_ids).map(id => text(id)).filter(Boolean))
}

function selectedServiceRows(row = {}, services = []) {
  const ids = selectedServiceIds(row)
  if (!ids.size) return []
  return asArray(services).filter(service => ids.has(text(service?.id)))
}

function selectedServiceResolution(row = {}, services = []) {
  const ids = selectedServiceIds(row)
  const rows = selectedServiceRows(row, services)
  const matchedIds = new Set(rows.map(service => text(service?.id)).filter(id => ids.has(id)))
  return {
    requestedCount: ids.size,
    matchedCount: matchedIds.size,
    rows,
  }
}

function plannedMinutesFromCurrentServices(services = []) {
  const rows = asArray(services)
  const total = rows.reduce((sum, row) => {
    const mins = numberOrNull(row?.expected_labour_minutes)
    return sum + Math.max(0, Math.round(mins || 0))
  }, 0)
  return total > 0 ? total : null
}

function serviceContextRows(context = {}) {
  if (Array.isArray(context)) return context
  return asArray(context.services || context.serviceRows)
}

export function isoDate(value = new Date()) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10)
  return date.toISOString().slice(0, 10)
}

export function addCalendarDays(dateStr, amount) {
  const date = new Date(`${isoDate(dateStr)}T12:00:00`)
  date.setDate(date.getDate() + Number(amount || 0))
  return date.toISOString().slice(0, 10)
}

export function calendarRange(startDate, dayCount = CALENDAR_RANGE_DAYS.twoWeeks) {
  const count = Math.max(1, Number(dayCount) || CALENDAR_RANGE_DAYS.twoWeeks)
  return Array.from({ length: count }, (_, index) => addCalendarDays(startDate, index))
}

export function weekStart(dateStr) {
  const date = new Date(`${isoDate(dateStr)}T12:00:00`)
  const day = date.getDay()
  const diff = day === 0 ? -6 : 1 - day
  date.setDate(date.getDate() + diff)
  return date.toISOString().slice(0, 10)
}

export function plannedMinutesFromServiceSnapshot(snapshot) {
  const rows = Array.isArray(snapshot) ? snapshot : []
  const total = rows.reduce((sum, row) => {
    const mins = numberOrNull(row?.expected_labour_minutes)
    return sum + Math.max(0, Math.round(mins || 0))
  }, 0)
  return total > 0 ? total : null
}

export function plannedMinutesForJob(job = {}, context = {}) {
  const current = selectedServiceResolution(job, serviceContextRows(context))
  if (current.requestedCount > 0 && current.matchedCount === current.requestedCount) {
    return plannedMinutesFromCurrentServices(current.rows)
  }
  return plannedMinutesFromServiceSnapshot(job.service_snapshot)
}

export function plannedMinutesForBooking(request = {}, context = {}) {
  const current = selectedServiceResolution(request, serviceContextRows(context))
  if (current.requestedCount > 0 && current.matchedCount === current.requestedCount) {
    return plannedMinutesFromCurrentServices(current.rows)
  }
  const snapshot = normaliseBookingServiceSnapshot(request.service_snapshot)
  return plannedMinutesFromServiceSnapshot(snapshot)
}

export function formatPlannedMinutes(value) {
  const mins = numberOrNull(value)
  return mins && mins > 0 ? fmtDuration(Math.round(mins)) : 'Time required'
}

export function jobScheduledDate(job = {}) {
  return text(job.scheduled_date)
}

export function jobConsumesCapacity(job = {}, todayDate = isoDate(), context = {}) {
  const status = normaliseJobStatus(job.status)
  const jobDate = jobScheduledDate(job)
  const today = isoDate(todayDate)
  return Boolean(
    jobDate
    && !NON_CAPACITY_STATUSES.has(status)
    && (
      ACTIVE_CAPACITY_STATUSES.has(status)
      || (HISTORICAL_CAPACITY_STATUSES.has(status) && jobDate <= today)
    )
    && plannedMinutesForJob(job, context)
  )
}

export function bookingConsumesProvisionalCapacity(request = {}, context = {}) {
  return Boolean(
    request.preferred_date
    && text(request.status || 'pending') === 'pending'
    && plannedMinutesForBooking(request, context)
  )
}

export function technicianName(member = {}) {
  return text(member.full_name || member.name) || 'Unnamed technician'
}

export function technicianColour(member = {}, index = 0) {
  const colours = [1, 2, 3, 4, 5, 6, 7, 8].map(n => `var(--chart-${n})`)
  const value = text(member.calendar_colour)
  return /^#[0-9a-f]{6}$/i.test(value) ? value : colours[index % colours.length]
}

export function technicianForJob(job = {}, team = []) {
  const id = text(job.technician_id)
  const byId = id ? team.find(member => text(member.id) === id) : null
  if (byId) return byId
  const legacyName = text(job.technician).toLowerCase()
  if (!legacyName) return null
  return team.find(member => technicianName(member).toLowerCase() === legacyName) || null
}

export function jobNeedsScheduling(job = {}, context = {}) {
  const status = normaliseJobStatus(job.status)
  if (NON_CAPACITY_STATUSES.has(status)) return false
  if (!NEEDS_SCHEDULING_STATUSES.has(status)) return false
  return !jobScheduledDate(job) || !text(job.technician_id) || !plannedMinutesForJob(job, context)
}

export function bookingModuleKey(request = {}) {
  const rows = normaliseBookingServiceSnapshot(request.service_snapshot)
  const modules = new Set(rows.map(serviceModuleKey))
  if (modules.size === 1 && modules.has('general')) return 'general'
  return 'suspension'
}

export function calendarEntryForJob(job = {}, context = {}) {
  const team = context.team || []
  const customers = context.customers || []
  const components = context.components || []
  const bikes = context.bikes || []
  const services = serviceContextRows(context)
  const customer = customers.find(row => text(row.id) === text(job.customer_id))
  const component = components.find(row => text(row.id) === text(job.component_id))
  const bike = bikes.find(row => text(row.id) === text(job.bike_id))
  const moduleKey = isGeneralJob(job) ? 'general' : 'suspension'
  const technician = technicianForJob(job, team)
  const technicianIndex = Math.max(0, team.findIndex(member => text(member.id) === text(technician?.id)))
  const plannedMinutes = plannedMinutesForJob(job, { services })
  const scheduledDate = jobScheduledDate(job)
  return {
    id: job.id,
    type: 'job',
    date: scheduledDate,
    moduleKey,
    provisional: false,
    job,
    customer,
    component,
    bike,
    technician,
    technicianColour: technician ? technicianColour(technician, technicianIndex) : '',
    title: jobServiceScopeSummary(job) || (component ? [component.year, component.model].filter(Boolean).join(' ') : jobModuleLabel(job)),
    subtitle: [
      customer ? [customer.first_name, customer.last_name].filter(Boolean).join(' ') : 'Unknown customer',
      technician ? technicianName(technician) : text(job.technician) || 'Unassigned',
      plannedMinutes ? formatPlannedMinutes(plannedMinutes) : 'Time required',
    ].filter(Boolean).join(' - '),
    plannedMinutes,
  }
}

export function calendarEntryForBooking(request = {}, context = {}) {
  const moduleKey = bookingModuleKey(request)
  const plannedMinutes = plannedMinutesForBooking(request, context)
  return {
    id: request.id,
    type: 'booking',
    date: request.preferred_date,
    moduleKey,
    provisional: true,
    request,
    customer: null,
    component: null,
    bike: null,
    technician: null,
    technicianColour: '',
    title: request.service_summary || 'Booking request',
    subtitle: [
      request.customer_name || 'Customer',
      'Pending request',
      plannedMinutes ? formatPlannedMinutes(plannedMinutes) : 'Time required',
    ].filter(Boolean).join(' - '),
    plannedMinutes,
  }
}

export function buildCalendarEntries({ jobs = [], bookingRequests = [], customers = [], bikes = [], components = [], team = [], services = [] } = {}) {
  const jobEntries = jobs
    .filter(job => jobScheduledDate(job) && !NON_CAPACITY_STATUSES.has(normaliseJobStatus(job.status)))
    .map(job => calendarEntryForJob(job, { customers, bikes, components, team, services }))
  const bookingEntries = bookingRequests
    .filter(request => text(request.status || 'pending') === 'pending' && request.preferred_date)
    .map(request => calendarEntryForBooking(request, { services }))
  return [...jobEntries, ...bookingEntries].sort((a, b) => {
    if (a.date !== b.date) return String(a.date || '').localeCompare(String(b.date || ''))
    if (a.type !== b.type) return a.type === 'booking' ? -1 : 1
    return String(a.title || '').localeCompare(String(b.title || ''))
  })
}

export function filterCalendarEntries(entries = [], filters = {}) {
  const modules = filters.modules || { general: true, suspension: true }
  const technicianFilter = text(filters.technician || 'all')
  return entries.filter(entry => {
    if (modules[entry.moduleKey] === false) return false
    if (technicianFilter === 'all') return true
    if (technicianFilter === 'unassigned') return !entry.technician
    return text(entry.technician?.id) === technicianFilter
  })
}

export function buildCapacitySummary({ jobs = [], bookingRequests = [], workshop = {}, team = [], startDate, dayCount = CALENDAR_RANGE_DAYS.twoWeeks, today = isoDate(), services = [] } = {}) {
  const days = calendarRange(startDate, dayCount)
  const weekKeys = [...new Set(days.map(weekStart))]
  const workshopCapMinutes = Math.round(Math.max(0, Number(workshop?.weekly_bookable_hours ?? 30) || 0) * 60)
  const technicianMap = new Map(team.map((member, index) => [text(member.id), {
    id: member.id,
    name: technicianName(member),
    colour: technicianColour(member, index),
    capacityMinutes: member.weekly_bookable_hours === null || member.weekly_bookable_hours === undefined || member.weekly_bookable_hours === ''
      ? null
      : Math.round(Math.max(0, Number(member.weekly_bookable_hours) || 0) * 60),
    confirmedMinutes: 0,
  }]))

  const weeks = weekKeys.map(key => ({
    weekStart: key,
    confirmedMinutes: 0,
    provisionalMinutes: 0,
    capacityMinutes: workshopCapMinutes,
    remainingMinutes: workshopCapMinutes,
    utilisationPercent: 0,
    status: 'normal',
    technicians: new Map(Array.from(technicianMap.values()).map(member => [member.id, { ...member, confirmedMinutes: 0, status: 'normal' }])),
  }))
  const weekMap = new Map(weeks.map(week => [week.weekStart, week]))

  jobs.forEach(job => {
    if (!jobConsumesCapacity(job, today, { services })) return
    const mins = plannedMinutesForJob(job, { services }) || 0
    const week = weekMap.get(weekStart(jobScheduledDate(job)))
    if (!week) return
    week.confirmedMinutes += mins
    const techId = text(job.technician_id)
    if (techId && week.technicians.has(techId)) {
      week.technicians.get(techId).confirmedMinutes += mins
    }
  })

  bookingRequests.forEach(request => {
    if (!bookingConsumesProvisionalCapacity(request, { services })) return
    const week = weekMap.get(weekStart(request.preferred_date))
    if (week) week.provisionalMinutes += plannedMinutesForBooking(request, { services }) || 0
  })

  weeks.forEach(week => {
    const total = week.confirmedMinutes + week.provisionalMinutes
    week.remainingMinutes = week.capacityMinutes - total
    week.utilisationPercent = week.capacityMinutes > 0 ? Math.round((total / week.capacityMinutes) * 100) : total > 0 ? 100 : 0
    week.status = week.capacityMinutes <= 0 && total > 0
      ? 'over'
      : total > week.capacityMinutes
        ? 'over'
        : week.utilisationPercent >= 85
          ? 'nearly'
          : 'normal'
    week.technicians = Array.from(week.technicians.values()).map(member => {
      const utilisation = member.capacityMinutes && member.capacityMinutes > 0
        ? Math.round((member.confirmedMinutes / member.capacityMinutes) * 100)
        : member.capacityMinutes === 0 && member.confirmedMinutes > 0 ? 100 : 0
      const status = member.capacityMinutes === null
        ? 'normal'
        : member.capacityMinutes <= 0 && member.confirmedMinutes > 0
          ? 'over'
          : member.confirmedMinutes > member.capacityMinutes
            ? 'over'
            : utilisation >= 85
              ? 'nearly'
              : 'normal'
      return { ...member, utilisationPercent: utilisation, status }
    })
  })

  return weeks
}

export function needsSchedulingJobs(jobs = [], context = {}) {
  return jobs.filter(job => jobNeedsScheduling(job, context))
}
